import { ADVERTISING_CONFIG, type AdvertisingConfig } from './advertising.config';
import { CAMERA_CONFIG, type CameraConfig } from './camera.config';
import { CHARACTER_CONFIG, type CharacterConfig } from './character.config';
import { COLLECTIBLES_CONFIG, type CollectiblesConfig } from './collectibles.config';
import { DEBUG_CONFIG, type DebugConfig } from './debug.config';
import { DIFFICULTY_CONFIG, type DifficultyConfig } from './difficulty.config';
import { BUS_CONFIG, type BusConfig } from './bus.config';
import { DOZER_CONFIG, type DozerConfig } from './dozer.config';
import { EFFECTS_CONFIG, type EffectsConfig } from './effects.config';
import { EVENTS_CONFIG, type EventsConfig } from './events.config';
import { HAZARDS_CONFIG, type HazardsConfig } from './hazards.config';
import { INPUT_CONFIG, type InputConfig } from './input.config';
import { LEVEL_CONFIG, type LevelConfig } from './level.config';
import { MINIGAMES_CONFIG, type MiniGamesConfig } from './minigames.config';
import { PLAYER_CONFIG, type PlayerConfig } from './player.config';
import { PURSUIT_CONFIG, type PursuitConfig } from './pursuit.config';
import { QUIZ_CONFIG, type QuizConfig } from './quiz.config';
import { RENDERING_CONFIG, type RenderingConfig } from './rendering.config';
import { SCORING_CONFIG, type ScoringConfig } from './scoring.config';
import { UI_CONFIG, type UiConfig } from './ui.config';
import { WORLD_CONFIG, type WorldConfig } from './world.config';
import { ZONES_CONFIG, type ZonesConfig } from './zones.config';

/**
 * Single entry point for all tuning values. Systems receive the section they
 * need via constructor injection, so tests can pass modified configs.
 */
export interface GameConfig {
  readonly world: WorldConfig;
  readonly player: PlayerConfig;
  readonly character: CharacterConfig;
  readonly camera: CameraConfig;
  readonly input: InputConfig;
  readonly difficulty: DifficultyConfig;
  readonly level: LevelConfig;
  readonly collectibles: CollectiblesConfig;
  readonly scoring: ScoringConfig;
  readonly pursuit: PursuitConfig;
  readonly rendering: RenderingConfig;
  readonly advertising: AdvertisingConfig;
  readonly effects: EffectsConfig;
  readonly events: EventsConfig;
  readonly hazards: HazardsConfig;
  /** Dozer set piece (SEG_DOZER_60M): jump on, push earth into the pit, rewards. */
  readonly dozer: DozerConfig;
  /** ructec-Bulli (SEG_BUS_*): 1500 m drive from 1000 m on – traffic, roof load, ramps, rising speed. */
  readonly bus: BusConfig;
  /** Day/night zones and enclosed-stretch lighting (Phase 3). */
  readonly zones: ZonesConfig;
  /** Loading / start screen: game name, background images, footer, rendering behind the menus. */
  readonly ui: UiConfig;
  /** Bau-Quiz at the golden bottle (question pool, time, bonus). */
  readonly quiz: QuizConfig;
  /** Mini games at the golden bottle (crane, hammer & nail, concrete mixer) – drawn at random with the quiz. */
  readonly minigames: MiniGamesConfig;
  readonly debug: DebugConfig;
}

export const GAME_CONFIG: GameConfig = {
  world: WORLD_CONFIG,
  player: PLAYER_CONFIG,
  character: CHARACTER_CONFIG,
  camera: CAMERA_CONFIG,
  input: INPUT_CONFIG,
  difficulty: DIFFICULTY_CONFIG,
  level: LEVEL_CONFIG,
  collectibles: COLLECTIBLES_CONFIG,
  scoring: SCORING_CONFIG,
  pursuit: PURSUIT_CONFIG,
  rendering: RENDERING_CONFIG,
  advertising: ADVERTISING_CONFIG,
  effects: EFFECTS_CONFIG,
  events: EVENTS_CONFIG,
  hazards: HAZARDS_CONFIG,
  dozer: DOZER_CONFIG,
  bus: BUS_CONFIG,
  zones: ZONES_CONFIG,
  ui: UI_CONFIG,
  quiz: QUIZ_CONFIG,
  minigames: MINIGAMES_CONFIG,
  debug: DEBUG_CONFIG,
};

export type {
  AdvertisingConfig,
  CameraConfig,
  CharacterConfig,
  CollectiblesConfig,
  DebugConfig,
  DifficultyConfig,
  DozerConfig,
  BusConfig,
  EffectsConfig,
  EventsConfig,
  HazardsConfig,
  InputConfig,
  LevelConfig,
  MiniGamesConfig,
  PlayerConfig,
  PursuitConfig,
  RenderingConfig,
  ScoringConfig,
  UiConfig,
  WorldConfig,
  ZonesConfig,
};
